import { AnimatedContentLoader, LucaIcon, Shimmer } from '@components'
import { getDocumentsPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission, usePermissionsLoaded } from '@context'
import { useLocalStorage, useTheme } from '@hooks'
import {
  fetchAlerts,
  IDashboardAlert,
  useCurrentUser,
  dashboardQueryKey,
  dashboardQueryOptions
} from '@query'
import {
  businessDashboardAlertsLocalStorageKey,
  referralDashboardCardDismissedCacheKey
} from '@utils'
import { useQuery } from '@tanstack/react-query'
import { uniq } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'
import { useDashboardBootstrap, useDashboardOverview } from '../../hooks/useDashboardOverview'
import { Card } from '../shared/Card'
import { NeedsYouEmptyState } from './NeedsYouEmptyState'
import { NeedsYouRow, PartnerPill } from './NeedsYouRow'
import { ReferralPromoRow } from './ReferralPromoRow'
import { getAlertPresentation } from './alertPresentation'

const PLACEHOLDER_ROW_COUNT = 3

export const NeedsYouCard: React.FC = () => {
  const [t] = useTranslation()
  const theme = useTheme()
  const { slug } = useBusinessContext()
  const { data: currentUser } = useCurrentUser()
  const { data: overview } = useDashboardOverview()
  const bootstrap = useDashboardBootstrap()

  const [dismissedTypes, setDismissedTypes] = useLocalStorage(
    businessDashboardAlertsLocalStorageKey(slug),
    []
  )
  const [isReferralDismissed, setReferralDismissed] = useLocalStorage(
    referralDashboardCardDismissedCacheKey(currentUser?.id ?? ''),
    false
  )
  const permissionsLoaded = usePermissionsLoaded()
  const canViewBookkeeping = usePermission(PermissionSet.BOOKKEEPING_VIEW)

  const alertsQueryKey = dashboardQueryKey(slug, 'alerts')
  const { data: standaloneAlerts, isLoading: areStandaloneAlertsLoading } = useQuery({
    queryKey: alertsQueryKey,
    queryFn: () => fetchAlerts({ slug }),
    ...{
      enabled: Boolean(slug) && permissionsLoaded && !canViewBookkeeping,
      ...dashboardQueryOptions(slug)
    }
  })

  const alerts = canViewBookkeeping ? bootstrap.data?.alerts : standaloneAlerts
  const areAlertsLoading = canViewBookkeeping ? bootstrap.isLoading : areStandaloneAlertsLoading

  const isLoading = !slug || !permissionsLoaded || areAlertsLoading

  const visibleAlerts = (alerts ?? []).filter(alert => !dismissedTypes.includes(alert.type))
  const showReferral = currentUser?.id != null && !isReferralDismissed

  // Banner on drafts, not suggestions alone — Luca may not have run yet.
  const suggestedCount = overview?.documents?.suggested_count ?? 0
  const draftCount = overview?.documents?.draft_count ?? 0
  const hasSuggestions = suggestedCount > 0
  const showBanner = canViewBookkeeping && (hasSuggestions || draftCount > 0)
  const taskCount = visibleAlerts.length + (showReferral ? 1 : 0) + (showBanner ? 1 : 0)

  const dismiss = (alert: IDashboardAlert) =>
    setDismissedTypes(uniq([...dismissedTypes, alert.type]))

  return (
    <StretchCard
      title={t('dashboard.needsYou.title')}
      tooltip={t('dashboard.needsYou.tooltip')}
      headerMeta={
        taskCount > 0 ? (
          <CountBadge>{taskCount}</CountBadge>
        ) : !isLoading ? (
          <ClearBadge aria-label={t('dashboard.emptyAlert.description')}>
            <FaCheck size={9} aria-hidden />0
          </ClearBadge>
        ) : null
      }
    >
      <AnimatedContentLoader
        contentDisplay="flex"
        isLoading={isLoading}
        isEmpty={taskCount === 0}
        loadingContent={<NeedsYouShimmerList />}
        isEmptyContent={<NeedsYouEmptyState />}
      >
        <List>
          {visibleAlerts.map(alert => {
            const presentation = getAlertPresentation(alert, theme, slug)
            return (
              <NeedsYouRow
                key={alert.type}
                link={presentation.link}
                tint={presentation.tint}
                color={presentation.color}
                icon={presentation.icon}
                logo={presentation.logo}
                title={alert.title}
                meta={alert.message}
                badge={
                  presentation.isPartner ? (
                    <PartnerPill>{t('dashboard.needsYou.partner')}</PartnerPill>
                  ) : undefined
                }
                onDismiss={presentation.isDismissable ? () => dismiss(alert) : undefined}
              />
            )
          })}
          {showReferral && <ReferralPromoRow onDismiss={() => setReferralDismissed(true)} />}
          {showBanner && (
            <NeedsYouRow
              link={getDocumentsPageUrl(slug, { draft: '1' })}
              tint={theme.dashboard.infoTint}
              color={theme.colors.primary}
              icon={<LucaIcon size={18} />}
              title={
                hasSuggestions
                  ? t('dashboard.needsYou.banner.title', { count: suggestedCount })
                  : t('dashboard.needsYou.banner.draftTitle', { count: draftCount })
              }
              meta={
                hasSuggestions
                  ? t('dashboard.needsYou.banner.body')
                  : t('dashboard.needsYou.banner.draftBody')
              }
            />
          )}
        </List>
      </AnimatedContentLoader>
    </StretchCard>
  )
}

const NeedsYouShimmerList: React.FC = () => (
  <List>
    {Array.from({ length: PLACEHOLDER_ROW_COUNT }).map((_, index) => (
      <PlaceholderRow key={index} aria-hidden>
        <Shimmer width="2.125rem" height="2.125rem" borderRadius="0.6875rem" />
        <PlaceholderText>
          <Shimmer width="11rem" height="0.875rem" />
          <Shimmer width="8rem" height="0.75rem" />
        </PlaceholderText>
        <Shimmer width="1.75rem" height="1.75rem" borderRadius="0.5rem" />
      </PlaceholderRow>
    ))}
  </List>
)

const StretchCard = styled(Card)`
  min-width: 0;
  align-self: stretch;
  overflow: hidden;

  > *:not(:first-child) {
    flex: 1 1 auto;
    min-height: 0;
  }
`

const CountBadge = styled.span`
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.dashboard.infoTint};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
`

const ClearBadge = styled(CountBadge)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  background: ${({ theme }) => theme.dashboard.positiveTint};
  color: ${({ theme }) => theme.dashboard.positive};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}rem;

  > *:last-child {
    flex: 1 1 auto;
    border-bottom: none;
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }
`

const PlaceholderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md}rem;
  box-sizing: border-box;
  min-height: 4.5rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
`

const PlaceholderText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex: 1;
  align-self: stretch;
  min-width: 0;
`
